import { useState, useEffect, useRef } from 'react';
import { api } from '../api.js';

const QUICK_CLINICAL_CHIPS = [
  'ANTT Wound Dressing Clean & Dry',
  'Foley Catheter Patent (Urine Amber/Clear)',
  "Ryle's Tube Checked & Feeding Tolerated",
  'Repositioned Q2H & Skin Intact',
  'Prescribed Medications Administered',
  'Patient Alert & Communicative',
  'Afebrile & Well-Hydrated',
  'No Respiratory Distress / Dyspnea',
  'Diaper Changed & Perineal Hygiene Done',
  'Family Reassured & Home Care Briefed'
];

function extractLiveKeywords(text) {
  if (!text) return [];
  const tags = [];
  const bp = text.match(/(?:bp|blood pressure|tekanan darah|t\/d|血压)\s*(?:is|was|adalah|:)?\s*(\d{2,3})\s*(?:\/|over|\s)\s*(\d{2,3})/i) ||
             text.match(/\b(\d{2,3})\s*\/\s*(\d{2,3})\s*(?:mmhg)?\b/i);
  if (bp) tags.push(`BP ${bp[1]}/${bp[2]}`);

  const pulse = text.match(/(?:pulse|hr|heart rate|nadi|心率|脉搏)\s*(?:is|was|adalah|:)?\s*(\d{2,3})/i);
  if (pulse) tags.push(`Pulse ${pulse[1]} bpm`);

  const temp = text.match(/(?:temp|temperature|suhu|体温)\s*(?:is|was|adalah|:)?\s*(\d{2}(?:\.\d)?)/i);
  if (temp) tags.push(`Temp ${temp[1]}°C`);

  const spo2 = text.match(/(?:spo2|oxygen|oximeter|o2|sat|sats|oksigen|血氧)\s*(?:is|was|adalah|:)?\s*(\d{2,3})%?/i);
  if (spo2) tags.push(`SpO2 ${spo2[1]}%`);

  const sugar = text.match(/(?:sugar|glucose|dxt|bsp|hgt|gula|血糖)\s*(?:is|was|adalah|:)?\s*(\d{1,2}(?:\.\d)?)/i);
  if (sugar) tags.push(`Glucose ${sugar[1]} mmol/L`);

  const pain = text.match(/(?:pain|sakit|疼痛)\s*(?:score|is|:)?\s*(\d{1,2})/i);
  if (pain) tags.push(`Pain ${pain[1]}/10`);
  else if (/no pain|tidak sakit|tak sakit|无痛/i.test(text)) tags.push(`Pain 0/10 (None)`);

  const lower = text.toLowerCase();
  if (lower.includes('wound') || lower.includes('dress') || lower.includes('ulcer') || lower.includes('luka') || lower.includes('换药') || lower.includes('antt')) tags.push('ANTT Wound Care');
  if (lower.includes('catheter') || lower.includes('foley') || lower.includes('cbd') || lower.includes('kencing') || lower.includes('尿管') || lower.includes('urine')) tags.push('Foley Catheter');
  if (lower.includes('tube') || lower.includes('ng') || lower.includes('ryles') || lower.includes('feeding') || lower.includes('susu') || lower.includes('胃管')) tags.push("Ryle's Feeding");
  if (lower.includes('turn') || lower.includes('bed bath') || lower.includes('adl') || lower.includes('diaper') || lower.includes('lampin') || lower.includes('q2h')) tags.push('ADL / Q2H Turn');
  if (lower.includes('injection') || lower.includes('suntik') || lower.includes('clexane') || lower.includes('insulin') || lower.includes('打针')) tags.push('Injection Given');
  if (lower.includes('blood') || lower.includes('darah') || lower.includes('抽血') || lower.includes('sample') || lower.includes('lab')) tags.push('Blood Sample');
  if (lower.includes('reassur') || lower.includes('family') || lower.includes('briefed') || lower.includes('keluarga') || lower.includes('家属')) tags.push('Family Briefed');

  return tags;
}

export default function AiClinicalSuite({ caseObj, me, onBack }) {
  const [tab, setTab] = useState('voice');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(caseObj || null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  // Voice SBAR state
  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [sbarMode, setSbarMode] = useState('voice'); // 'voice' or 'blanks'
  const [blankVitals, setBlankVitals] = useState({
    bp_sys: '',
    bp_dia: '',
    pulse: '',
    temp: '',
    spo2: '',
    sugar: '',
    pain: '0',
    rr: '16'
  });
  const [selectedChips, setSelectedChips] = useState([
    'Patient Alert & Communicative',
    'Family Reassured & Home Care Briefed'
  ]);
  const [customBlankNote, setCustomBlankNote] = useState('');

  const recognitionRef = useRef(null);
  const keepListeningRef = useRef(false);

  useEffect(() => {
    return () => {
      keepListeningRef.current = false;
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_) {}
      }
    };
  }, []);

  // Wound AI state
  const [woundImg, setWoundImg] = useState('');
  const [woundLocation, setWoundLocation] = useState('Sacrum / Heel');
  const [woundType, setWoundType] = useState('Pressure Injury / Diabetic Ulcer');
  const [woundNotes, setWoundNotes] = useState('');

  // Med Scanner state
  const [medImg, setMedImg] = useState('');
  const [medText, setMedText] = useState('');

  // Family Summary state
  const [familyNotes, setFamilyNotes] = useState('');
  const [familyLang, setFamilyLang] = useState('en');

  // Doctor Briefing state
  const [docVitals, setDocVitals] = useState('');
  const [showLiveChart, setShowLiveChart] = useState(false);

  useEffect(() => {
    if (!caseObj) {
      api.getCases('status=active').then((d) => {
        const list = d.cases || [];
        setCases(list);
        if (list.length && !selectedCase) setSelectedCase(list[0]);
      }).catch(() => {});
    }
  }, [caseObj]);

  // Speech Recognition hook with auto-resume so nurses can speak smoothly
  function toggleListen() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech Recognition is not supported in this browser. Please use the "Guided Fill-in-the-Blanks" tab or type your notes.');
      return;
    }
    if (isListening) {
      keepListeningRef.current = false;
      setIsListening(false);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (_) {}
      }
      return;
    }
    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-MY';

      recognition.onstart = () => {
        setIsListening(true);
      };
      recognition.onend = () => {
        // Auto-restart if user has not intentionally stopped, allowing smooth natural speech with pauses
        if (keepListeningRef.current) {
          try {
            recognition.start();
          } catch (_) {
            setIsListening(false);
          }
        } else {
          setIsListening(false);
        }
      };
      recognition.onerror = (e) => {
        if (e.error === 'no-speech' && keepListeningRef.current) {
          return; // brief silence while taking vitals, ignore
        }
        if (e.error === 'not-allowed') {
          keepListeningRef.current = false;
          setIsListening(false);
          alert('Microphone access denied. Please allow microphone permissions or switch to Guided Fill-in-the-Blanks.');
        }
      };
      recognition.onresult = (event) => {
        let current = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            current += event.results[i][0].transcript + ' ';
          }
        }
        if (current) {
          setTranscript((prev) => (prev ? prev.trim() + ' ' : '') + current.trim());
        }
      };

      keepListeningRef.current = true;
      recognitionRef.current = recognition;
      recognition.start();
    } catch (e) {
      setIsListening(false);
      keepListeningRef.current = false;
      alert('Could not start microphone: ' + e.message);
    }
  }

  // Handle Image Upload
  function handleImageUpload(e, setter) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setter(reader.result);
    reader.readAsDataURL(file);
  }

  // Actions
  async function runVoiceLogger() {
    if (!transcript.trim()) return;
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await api.callVoiceLogger(transcript);
      setResult(res);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  async function runWoundAi() {
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await api.callWoundAi({
        image: woundImg,
        location: woundLocation,
        woundType,
        notes: woundNotes
      });
      setResult(res);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  async function runMedScanner() {
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await api.callMedScanner({ image: medImg, text: medText });
      setResult(res);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  async function runFamilySummary() {
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await api.callFamilySummary({
        clinicalNotes: familyNotes || selectedCase?.notes || '',
        vitals: selectedCase?.vitals || {},
        lang: familyLang
      });
      setResult(res);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  async function runDoctorBriefing() {
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await api.callDoctorBriefing({
        case_id: selectedCase?.id || '',
        name: selectedCase?.name || 'Mr. Lee Hock Seng (李福星)',
        ic: selectedCase?.ic || selectedCase?.nric || '',
        diagnosis: selectedCase?.care_type || selectedCase?.notes || 'Post-Hospital Discharge · Stroke Recovery & ANTT Wound Care',
        allergies: selectedCase?.allergies || 'NKDA (No Known Drug Allergies)',
        vitals: selectedCase?.vitals || []
      });
      setResult(res);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  return (
    <div className="card" style={{ maxWidth: 1040, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onBack && <button className="link" onClick={onBack}>← Back</button>}
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>🧠</span> Bedside AI Clinical Suite
            </h2>
            <span className="cbadge proc" style={{ background: 'linear-gradient(135deg, #0284c7, #0d9488)', color: '#fff' }}>
              ANTT &amp; SBAR Powered
            </span>
          </div>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Interactive AI assistants for visiting nurses: voice-to-SBAR notes, ANTT wound vision, prescription OCR &amp; family WhatsApp updates.
          </p>
        </div>

        {/* Case Selector */}
        {!caseObj && cases.length > 0 && (
          <div>
            <select
              value={selectedCase?.id || ''}
              onChange={(e) => setSelectedCase(cases.find((c) => c.id === e.target.value) || null)}
              style={{ padding: '7px 12px', borderRadius: 8, background: '#0a1929', color: '#fff', border: '1px solid rgba(56, 189, 248, 0.3)' }}
            >
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  Patient: {c.name} ({c.care_type || 'General'})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 18 }}>
        {[
          { k: 'voice', l: '🎙️ Voice-to-SBAR Logger' },
          { k: 'wound', l: '🩹 ANTT Wound Vision AI' },
          { k: 'ocr', l: '📋 Prescription OCR' },
          { k: 'family', l: '💬 Family WhatsApp Advisor' },
          { k: 'doctor', l: '📄 1-Page Doctor Briefing' },
        ].map((t) => (
          <button
            key={t.k}
            className={'tab' + (tab === t.k ? ' on' : '')}
            onClick={() => { setTab(t.k); setResult(null); setErr(''); }}
          >
            {t.l}
          </button>
        ))}
      </div>

      {err && <div className="status" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #f87171' }}>{err}</div>}

      {/* Tab 1: Voice SBAR & Guided Fill-In-The-Blanks */}
      {tab === 'voice' && (
        <div>
          {/* Sub-mode selector */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
            <div style={{ display: 'inline-flex', background: 'var(--card-subtle, #f1f5f9)', padding: 4, borderRadius: 10, border: '1px solid var(--line)' }}>
              <button
                type="button"
                className={'ghost sm ' + (sbarMode === 'voice' ? 'pri' : '')}
                style={{
                  borderRadius: 7,
                  padding: '6px 14px',
                  fontWeight: 600,
                  fontSize: '0.84rem',
                  border: sbarMode === 'voice' ? 'none' : '1px solid transparent',
                  background: sbarMode === 'voice' ? '#0284c7' : 'transparent',
                  color: sbarMode === 'voice' ? '#fff' : 'inherit'
                }}
                onClick={() => setSbarMode('voice')}
              >
                🎙️ Voice Dictation
              </button>
              <button
                type="button"
                className={'ghost sm ' + (sbarMode === 'blanks' ? 'pri' : '')}
                style={{
                  borderRadius: 7,
                  padding: '6px 14px',
                  fontWeight: 600,
                  fontSize: '0.84rem',
                  border: sbarMode === 'blanks' ? 'none' : '1px solid transparent',
                  background: sbarMode === 'blanks' ? '#0284c7' : 'transparent',
                  color: sbarMode === 'blanks' ? '#fff' : 'inherit'
                }}
                onClick={() => setSbarMode('blanks')}
              >
                📝 Guided Fill-In-The-Blanks (填空速记)
              </button>
            </div>

            {sbarMode === 'voice' && (
              <button
                className="ghost sm"
                style={{
                  background: isListening ? '#ef4444' : '#0284c7',
                  color: '#fff',
                  borderColor: 'transparent',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontWeight: 600
                }}
                onClick={toggleListen}
              >
                {isListening ? '🛑 Stop Recording' : '🎙️ Start Voice Dictation'}
              </button>
            )}
          </div>

          {/* Sub-mode 1: Voice Dictation */}
          {sbarMode === 'voice' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: '0.86rem', color: 'var(--text-muted)' }}>
                  Bedside Spoken Dictation (English / Malay / Chinese mixed speech supported)
                </span>
                {isListening && (
                  <span style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: 700, animation: 'pulse 1.5s infinite' }}>
                    ● Listening smoothly… take your time
                  </span>
                )}
              </div>
              <textarea
                rows={5}
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder="Speak or type bedside observations: e.g. Patient BP 138/84, pulse 76, spo2 97%, blood sugar 6.2. Wound dressed with normal saline and hydrofiber. Patient alert, family reassured..."
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1px solid var(--line)', boxSizing: 'border-box' }}
              />

              {/* Live Keyword Extractor Pills */}
              {(() => {
                const liveKw = extractLiveKeywords(transcript);
                if (!liveKw || liveKw.length === 0) return null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 8, padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                    <span style={{ fontSize: '0.76rem', color: '#0369a1', fontWeight: 700 }}>🎯 Caught Live:</span>
                    {liveKw.map((kw, idx) => (
                      <span key={idx} style={{ fontSize: '0.74rem', background: '#fff', color: '#0284c7', border: '1px solid #38bdf8', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>
                        ✓ {kw}
                      </span>
                    ))}
                  </div>
                );
              })()}

              <button
                className="pri"
                style={{ marginTop: 12 }}
                onClick={runVoiceLogger}
                disabled={loading || !transcript.trim()}
              >
                {loading ? 'Structuring SBAR Note…' : '⚡ Convert to Structured SBAR Clinical Note'}
              </button>
            </div>
          )}

          {/* Sub-mode 2: Guided Fill-In-The-Blanks */}
          {sbarMode === 'blanks' && (
            <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 12, padding: 16 }}>
              <div style={{ marginBottom: 14 }}>
                <b style={{ color: '#0f172a', fontSize: '0.94rem' }}>Bedside Quick-Tap Form</b>
                <p className="muted" style={{ margin: '2px 0 0', fontSize: '0.82rem' }}>
                  Tap numbers and quick clinical chips without typing long paragraphs on your phone.
                </p>
              </div>

              {/* Vitals Form Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 3 }}>BP Systolic</label>
                  <input
                    type="number"
                    placeholder="e.g. 120"
                    value={blankVitals.bp_sys}
                    onChange={(e) => setBlankVitals({ ...blankVitals, bp_sys: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 3 }}>BP Diastolic</label>
                  <input
                    type="number"
                    placeholder="e.g. 80"
                    value={blankVitals.bp_dia}
                    onChange={(e) => setBlankVitals({ ...blankVitals, bp_dia: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 3 }}>Pulse (bpm)</label>
                  <input
                    type="number"
                    placeholder="e.g. 76"
                    value={blankVitals.pulse}
                    onChange={(e) => setBlankVitals({ ...blankVitals, pulse: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 3 }}>Temp (°C)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 36.8"
                    value={blankVitals.temp}
                    onChange={(e) => setBlankVitals({ ...blankVitals, temp: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 3 }}>SpO2 (%)</label>
                  <input
                    type="number"
                    placeholder="e.g. 98"
                    value={blankVitals.spo2}
                    onChange={(e) => setBlankVitals({ ...blankVitals, spo2: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 3 }}>Glucose (mmol/L)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="e.g. 6.2"
                    value={blankVitals.sugar}
                    onChange={(e) => setBlankVitals({ ...blankVitals, sugar: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 3 }}>Pain (0–10)</label>
                  <select
                    value={blankVitals.pain}
                    onChange={(e) => setBlankVitals({ ...blankVitals, pain: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                  >
                    {[0,1,2,3,4,5,6,7,8,9,10].map((v) => (
                      <option key={v} value={v}>{v === 0 ? '0 (No Pain)' : `${v}/10`}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Quick-Tap Clinical Chips */}
              <div style={{ marginBottom: 14 }}>
                <span style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 6, color: '#334155' }}>
                  Tap Standard Bedside Findings (1-Click Selection):
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {QUICK_CLINICAL_CHIPS.map((chip, idx) => {
                    const active = selectedChips.includes(chip);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          if (active) {
                            setSelectedChips(selectedChips.filter((c) => c !== chip));
                          } else {
                            setSelectedChips([...selectedChips, chip]);
                          }
                        }}
                        style={{
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          padding: '5px 11px',
                          borderRadius: 20,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          border: active ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                          background: active ? '#e0f2fe' : '#ffffff',
                          color: active ? '#0369a1' : '#475569'
                        }}
                      >
                        {active ? '✓ ' : '+ '} {chip}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Additional Remarks */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: 4, color: '#334155' }}>
                  Any Specific Notes (Optional):
                </label>
                <input
                  type="text"
                  placeholder="e.g. Next visit change Foley catheter, requested extra gauze from pharmacy..."
                  value={customBlankNote}
                  onChange={(e) => setCustomBlankNote(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1' }}
                />
              </div>

              {/* Action Button */}
              <button
                type="button"
                className="pri"
                style={{ width: '100%', padding: '10px 16px', fontWeight: 700 }}
                onClick={async () => {
                  const parts = [];
                  if (blankVitals.bp_sys && blankVitals.bp_dia) parts.push(`BP ${blankVitals.bp_sys}/${blankVitals.bp_dia} mmHg`);
                  else if (blankVitals.bp_sys) parts.push(`BP ${blankVitals.bp_sys}`);
                  if (blankVitals.pulse) parts.push(`pulse ${blankVitals.pulse} bpm`);
                  if (blankVitals.temp) parts.push(`temp ${blankVitals.temp}°C`);
                  if (blankVitals.spo2) parts.push(`SpO2 ${blankVitals.spo2}%`);
                  if (blankVitals.sugar) parts.push(`blood sugar ${blankVitals.sugar} mmol/L`);
                  if (blankVitals.pain) parts.push(`pain score ${blankVitals.pain}/10`);

                  const vitalsStr = parts.length > 0 ? `Bedside vitals: ${parts.join(', ')}. ` : '';
                  const obsStr = selectedChips.length > 0 ? `Clinical observations: ${selectedChips.join('; ')}. ` : '';
                  const noteStr = customBlankNote.trim() ? `Remarks: ${customBlankNote.trim()}.` : '';
                  const compiled = `${vitalsStr}${obsStr}${noteStr}`.trim();

                  if (!compiled) {
                    alert('Please enter at least one vital sign or select a clinical chip.');
                    return;
                  }

                  setTranscript(compiled);
                  setLoading(true); setErr(''); setResult(null);
                  try {
                    const res = await api.callVoiceLogger(compiled);
                    setResult(res);
                  } catch (e) {
                    setErr(e.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                {loading ? 'Structuring SBAR Note…' : '⚡ Compile Blanks & Generate SBAR Note'}
              </button>
            </div>
          )}

          {/* SBAR Output Card */}
          {result && result.sbar && (
            <div style={{ marginTop: 20, background: '#f8fafc', border: '1.5px solid #0284c7', borderRadius: 12, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <b style={{ color: '#0369a1', fontSize: '1rem' }}>✓ Structured SBAR Note Generated</b>
                <button
                  className="ghost sm"
                  onClick={() => {
                    navigator.clipboard.writeText(result.formattedNote || JSON.stringify(result.sbar, null, 2));
                    alert('SBAR Note copied to clipboard!');
                  }}
                >
                  📋 Copy Note
                </button>
              </div>

              {/* Vitals parsed badge strip */}
              {result.vitals && Object.values(result.vitals).some(Boolean) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  {result.vitals.bp_sys && <span className="cbadge proc">BP: {result.vitals.bp_sys}/{result.vitals.bp_dia || '80'} mmHg</span>}
                  {result.vitals.pulse && <span className="cbadge proc">Pulse: {result.vitals.pulse} bpm</span>}
                  {result.vitals.spo2 && <span className="cbadge proc">SpO2: {result.vitals.spo2}%</span>}
                  {result.vitals.temp && <span className="cbadge proc">Temp: {result.vitals.temp}°C</span>}
                  {result.vitals.temperature && !result.vitals.temp && <span className="cbadge proc">Temp: {result.vitals.temperature}°C</span>}
                  {result.vitals.sugar && <span className="cbadge proc">Glucose: {result.vitals.sugar} mmol/L</span>}
                  {result.vitals.mews !== null && result.vitals.mews !== undefined && (
                    <span className="cbadge lt" style={{ fontWeight: 700 }}>
                      MEWS: {result.vitals.mews} ({result.vitals.mews >= 3 ? '🔴 Alert' : result.vitals.mews >= 2 ? '🟡 Moderate' : '🟢 Normal'})
                    </span>
                  )}
                </div>
              )}

              {/* Detected Keywords and Procedures */}
              {((result.detectedKeywords && result.detectedKeywords.length > 0) || (result.procedures && result.procedures.length > 0)) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {result.detectedKeywords?.map((kw, i) => (
                    <span key={'kw-' + i} style={{ fontSize: '0.76rem', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>
                      ✓ {kw}
                    </span>
                  ))}
                  {result.procedures?.map((proc, i) => (
                    <span key={'pr-' + i} style={{ fontSize: '0.76rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 999, padding: '2px 8px', fontWeight: 600 }}>
                      🏥 {proc}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <b style={{ color: '#0284c7', display: 'block', marginBottom: 4 }}>Situation (S)</b>
                  <p style={{ margin: 0, fontSize: '0.84rem' }}>{result.sbar.situation || '—'}</p>
                </div>
                <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <b style={{ color: '#0284c7', display: 'block', marginBottom: 4 }}>Background (B)</b>
                  <p style={{ margin: 0, fontSize: '0.84rem' }}>{result.sbar.background || '—'}</p>
                </div>
                <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <b style={{ color: '#0284c7', display: 'block', marginBottom: 4 }}>Assessment (A)</b>
                  <p style={{ margin: 0, fontSize: '0.84rem' }}>{result.sbar.assessment || '—'}</p>
                </div>
                <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <b style={{ color: '#0284c7', display: 'block', marginBottom: 4 }}>Recommendation (R)</b>
                  <p style={{ margin: 0, fontSize: '0.84rem' }}>{result.sbar.recommendation || '—'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Wound Vision AI */}
      {tab === 'wound' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '0.82rem', marginBottom: 4 }}>Wound Location</label>
              <input
                type="text"
                value={woundLocation}
                onChange={(e) => setWoundLocation(e.target.value)}
                placeholder="e.g. Left Sacrum, Right Lateral Malleolus"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '0.82rem', marginBottom: 4 }}>Etiology / Classification</label>
              <select
                value={woundType}
                onChange={(e) => setWoundType(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="Pressure Injury (NPUAP / EPUAP)">Pressure Injury (NPUAP / EPUAP)</option>
                <option value="Diabetic Foot Ulcer (DFU)">Diabetic Foot Ulcer (DFU)</option>
                <option value="Venous Leg Ulcer">Venous Leg Ulcer</option>
                <option value="Arterial Ulcer">Arterial Ulcer</option>
                <option value="Post-Surgical Dehiscence">Post-Surgical Dehiscence</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.82rem', marginBottom: 4 }}>Capture / Upload Bedside Wound Photo</label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleImageUpload(e, setWoundImg)}
              style={{ display: 'block', marginBottom: 8 }}
            />
            {woundImg && (
              <img
                src={woundImg}
                alt="Wound preview"
                style={{ maxHeight: 180, borderRadius: 8, border: '1px solid #cbd5e1', objectFit: 'contain' }}
              />
            )}
          </div>

          <button
            className="pri"
            onClick={runWoundAi}
            disabled={loading}
          >
            {loading ? 'Analyzing ANTT Tissue Breakdown…' : '⚡ Run Bedside ANTT Wound Vision AI'}
          </button>

          {result && (
            <div style={{ marginTop: 20, background: '#f8fafc', border: '1.5px solid #0d9488', borderRadius: 12, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <b style={{ color: '#0f766e', fontSize: '1rem' }}>✓ Bedside ANTT Wound Assessment Complete</b>
                <span className="cbadge proc">{result.source || 'ANTT Engine'}</span>
              </div>

              {/* Tissue breakdown progress bars */}
              {result.tissueBreakdown && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '0.84rem', fontWeight: 700, marginBottom: 6 }}>Tissue Composition Analysis:</div>
                  <div style={{ display: 'flex', height: 18, borderRadius: 8, overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ width: `${result.tissueBreakdown.granulation || 60}%`, background: '#ef4444' }} title="Granulation" />
                    <div style={{ width: `${result.tissueBreakdown.slough || 30}%`, background: '#eab308' }} title="Slough" />
                    <div style={{ width: `${result.tissueBreakdown.necrosis || 10}%`, background: '#1e293b' }} title="Necrosis" />
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem', color: '#475569' }}>
                    <span style={{ color: '#b91c1c', fontWeight: 700 }}>■ Granulation: {result.tissueBreakdown.granulation || 60}%</span>
                    <span style={{ color: '#a16207', fontWeight: 700 }}>■ Slough: {result.tissueBreakdown.slough || 30}%</span>
                    <span style={{ color: '#0f172a', fontWeight: 700 }}>■ Necrosis: {result.tissueBreakdown.necrosis || 10}%</span>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <b style={{ color: '#0d9488', display: 'block', fontSize: '0.82rem' }}>Stage / Depth</b>
                  <span style={{ fontWeight: 700, fontSize: '1rem' }}>{result.stage || 'Stage II (Partial Thickness)'}</span>
                </div>
                <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <b style={{ color: '#0d9488', display: 'block', fontSize: '0.82rem' }}>Exudate Level</b>
                  <span style={{ fontWeight: 700, fontSize: '1rem' }}>{result.exudate || 'Moderate (Serosanguinous)'}</span>
                </div>
                <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <b style={{ color: '#0d9488', display: 'block', fontSize: '0.82rem' }}>Periwound Skin</b>
                  <span style={{ fontWeight: 700, fontSize: '1rem' }}>{result.periwound || 'Intact with mild erythema'}</span>
                </div>
              </div>

              {result.recommendedDressing && (
                <div style={{ marginTop: 12, background: '#f0fdfa', border: '1px solid #5eead4', padding: 12, borderRadius: 8 }}>
                  <b style={{ color: '#0f766e', display: 'block', marginBottom: 4 }}>MOH ANTT Dressing Protocol Recommendation:</b>
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>{result.recommendedDressing}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Prescription OCR Scanner */}
      {tab === 'ocr' && (
        <div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.82rem', marginBottom: 4 }}>Hospital Discharge Slip / Medication Label Photo</label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleImageUpload(e, setMedImg)}
              style={{ display: 'block', marginBottom: 8 }}
            />
            {medImg && (
              <img
                src={medImg}
                alt="Prescription preview"
                style={{ maxHeight: 180, borderRadius: 8, border: '1px solid #cbd5e1', objectFit: 'contain' }}
              />
            )}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.82rem', marginBottom: 4 }}>Or Type / Paste Medication List</label>
            <textarea
              rows={4}
              value={medText}
              onChange={(e) => setMedText(e.target.value)}
              placeholder="e.g. Amlodipine 5mg OD OM (Morning) for BP&#10;Metformin 500mg BD PC for Diabetes&#10;Atorvastatin 20mg ON (Night) for Cholesterol"
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid var(--line)', boxSizing: 'border-box' }}
            />
          </div>

          <button
            className="pri"
            onClick={runMedScanner}
            disabled={loading || (!medImg && !medText.trim())}
          >
            {loading ? 'Parsing Prescription & Timeslots…' : '⚡ OCR Scan & Auto-Map to MAR Timeslots'}
          </button>

          {result && result.medications && (
            <div style={{ marginTop: 20, background: '#f8fafc', border: '1.5px solid #0284c7', borderRadius: 12, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <b style={{ color: '#0369a1', fontSize: '1rem' }}>✓ {result.medications.length} Medications Successfully Parsed</b>
                <span className="cbadge proc">Refill alert: {result.refillAlertDays || 7} days</span>
              </div>

              <div style={{ display: 'grid', gap: 8 }}>
                {result.medications.map((m, idx) => (
                  <div key={idx} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <b style={{ color: '#0f172a', fontSize: '0.92rem' }}>{m.name}</b>
                      <span className="wbadge" style={{ marginLeft: 8 }}>{m.dosage}</span>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
                        Freq: {m.frequency} {m.instructions ? `· ${m.instructions}` : ''}
                      </div>
                    </div>
                    <div>
                      <span className="cbadge lt" style={{ textTransform: 'uppercase' }}>{m.slot || 'Daily'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Family WhatsApp Summary */}
      {tab === 'family' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
            <label style={{ fontWeight: 700, fontSize: '0.85rem' }}>Clinical Observations to Translate for Family</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>Language:</span>
              <select
                value={familyLang}
                onChange={(e) => setFamilyLang(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 6 }}
              >
                <option value="en">English (Warm & Reassuring)</option>
                <option value="zh">中文 (温馨孝亲版)</option>
                <option value="bm">Bahasa Melayu (Mesra & Tenang)</option>
                <option value="ta">தமிழ் (அன்பான குடும்ப சுருக்கம்)</option>
              </select>
            </div>
          </div>

          <textarea
            rows={4}
            value={familyNotes}
            onChange={(e) => setFamilyNotes(e.target.value)}
            placeholder="e.g. Patient was in good spirits today. Ate 75% of lunch. Normal saline wound dressing applied to left heel, granulation tissue healthy. Blood pressure stable at 128/78."
            style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid var(--line)', boxSizing: 'border-box' }}
          />

          <button
            className="pri"
            style={{ marginTop: 12 }}
            onClick={runFamilySummary}
            disabled={loading}
          >
            {loading ? 'Crafting Family WhatsApp Summary…' : '⚡ Generate Compassionate Family WhatsApp Update'}
          </button>

          {result && result.summary && (
            <div style={{ marginTop: 20, background: '#f0fdf4', border: '1.5px solid #22c55e', borderRadius: 12, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <b style={{ color: '#15803d', fontSize: '1rem' }}>✓ Family WhatsApp Update Ready to Send</b>
                <button
                  className="ghost sm"
                  style={{ background: '#22c55e', color: '#fff', borderColor: 'transparent' }}
                  onClick={() => {
                    const text = encodeURIComponent(result.summary);
                    const phone = (selectedCase?.phone || '').replace(/[^0-9]/g, '');
                    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
                  }}
                >
                  💬 Open in WhatsApp
                </button>
              </div>

              <div style={{ background: '#fff', padding: 14, borderRadius: 8, border: '1px solid #bbf7d0', fontSize: '0.9rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {result.summary}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 5: Doctor Briefing */}
      {tab === 'doctor' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <p className="muted" style={{ margin: 0, maxWidth: 650 }}>
              Generates a hospital-grade teleconsultation briefing pack with the <b>Full Official MEWS Vital Signs Scoring Chart (14-Reading Clinical Sheet)</b>, hemodynamic metrics (MAP, Shock Index, SIRS), active MAR audit &amp; wound status.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className={'ghost sm' + (showLiveChart ? ' on' : '')}
                style={{
                  background: showLiveChart ? '#0C3054' : '#fff',
                  color: showLiveChart ? '#fff' : '#0C3054',
                  borderColor: '#0C3054',
                  fontWeight: 700
                }}
                onClick={() => setShowLiveChart(!showLiveChart)}
              >
                {showLiveChart ? '📋 Show Briefing Document' : '📊 Open Live MEWS Chart Grid'}
              </button>
            </div>
          </div>

          {showLiveChart ? (
            <div style={{ border: '1.5px solid #0C3054', borderRadius: 10, overflow: 'hidden', background: '#fff', marginTop: 12 }}>
              <div style={{ background: '#0C3054', color: '#fff', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <b style={{ fontSize: '0.95rem' }}>📊 Interactive MEWS Vital Signs Scoring Chart · {selectedCase?.name || 'Patient'}</b>
                <a
                  href={`/mews.html?case=${selectedCase?.id || ''}&name=${encodeURIComponent(selectedCase?.name || '')}&ic=${encodeURIComponent(selectedCase?.ic || '')}`}
                  target="_blank"
                  rel="noopener"
                  style={{ color: '#38bdf8', fontSize: '0.8rem', textDecoration: 'none', fontWeight: 700 }}
                >
                  ↗ Open in Full Screen
                </a>
              </div>
              <iframe
                title="Live MEWS Chart"
                src={`/mews.html?case=${selectedCase?.id || ''}&name=${encodeURIComponent(selectedCase?.name || '')}&ic=${encodeURIComponent(selectedCase?.ic || '')}`}
                style={{ width: '100%', height: 680, border: 'none', display: 'block' }}
              />
            </div>
          ) : (
            <div>
              <button
                className="pri"
                onClick={runDoctorBriefing}
                disabled={loading}
              >
                {loading ? 'Compiling Full Doctor Briefing & MEWS Chart…' : '⚡ Generate 1-Page Attending Physician Briefing Pack (with Full MEWS Chart)'}
              </button>

              {result && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                    <b style={{ color: '#0369a1', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>✓</span> Attending Physician Briefing Pack Ready (with Full MEWS Chart)
                    </b>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        className="ghost sm"
                        style={{ background: '#25D366', color: '#fff', borderColor: 'transparent', fontWeight: 700 }}
                        onClick={() => {
                          const docPhone = prompt('Enter Attending Doctor WhatsApp Number (e.g. 6012xxxxxxx):', '60122064868');
                          if (docPhone) {
                            const link = location.origin + (result.liveMewsUrl || `/mews.html?name=${encodeURIComponent(selectedCase?.name || 'Patient')}`);
                            const text = encodeURIComponent(`Dear Doctor, please find the Assura Teleconsultation Clinical Briefing Pack and Full MEWS Vital Chart for ${selectedCase?.name || 'our patient'}:\n\nDiagnosis: ${selectedCase?.care_type || 'Home Care'}\nPeak MEWS: ${result.maxMews || 0}\n\nLive Chart & Briefing: ${link}`);
                            window.open(`https://wa.me/${docPhone.replace(/[^0-9]/g, '')}?text=${text}`, '_blank');
                          }
                        }}
                      >
                        💬 Share to Doctor (WhatsApp)
                      </button>
                      <button className="ghost sm" onClick={() => window.print()}>🖨️ Print / Save as PDF</button>
                    </div>
                  </div>

                  <div
                    style={{ background: '#fff', borderRadius: 8, padding: 8, border: '1px solid #cbd5e1', overflowX: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: result.briefingHtml || result.html || result.briefing || '<p>Teleconsult briefing pack compiled.</p>' }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
